// src/components/icons.js
// ─────────────────────────────────────────────────────────────────────────────
// Icon registry.
//
// The app used to render Material Symbols as icon-font LIGATURES: the element's
// text content was the icon name, and the webfont substituted a glyph once it
// loaded. On a slow connection — which is the normal case on the Limbe–Idenau
// road — the font arrived late and users read raw "water_drop", "how_to_reg",
// "lock_reset" down the screen before it resolved. The app looked broken at
// exactly the moment a first-time visitor was deciding whether to trust it.
//
// Every icon is now an inline SVG from lucide-react, so it is part of the JS
// bundle and paints with the component. There is no font to wait for and no
// flash of text to see. The Google Fonts <link> for Material Symbols has been
// removed from public/index.html so it cannot come back.
//
// The map below is keyed by the ORIGINAL Material Symbol names. That is
// deliberate: those names are baked into ~190 call sites and, more importantly,
// into data files (schema.js hazard types, roles.js, healthMessages.js,
// constants.js badges) where an icon name is a stored value. Renaming them all
// would have been a large mechanical diff across files that have nothing to do
// with this change, and would have broken any record already carrying one.
// ─────────────────────────────────────────────────────────────────────────────

import {
  Activity, Airplay, AlarmClock, Archive, ArrowLeft, ArrowRight, Award, Backpack, BadgeCheck,
  BellRing, Bell, BellOff, BookOpen, Boxes, Brush, Building2, CalendarDays, Camera, ChartColumn,
  ChartLine, ChartPie, Check, CheckCheck, ChevronRight, CircleAlert, CircleCheck, CircleHelp,
  CirclePlus, CircleX, ClipboardCheck, ClipboardList, Clock, Cloud, CloudOff, Compass, Copy,
  Crosshair, Database, Download, Droplet, Droplets, Eye, EyeOff, FileDown, FileText, Files,
  Filter, Fish, Flag, Flame, FlaskConical, Gavel, Gem, Gift, Globe, GraduationCap, Grid2x2,
  Hammer,
  HandHeart, HeartPulse, House, IdCard, Image, Info, Languages, LayoutGrid, Layers, Leaf, Link2,
  ListChecks, LoaderCircle, LockKeyhole, LogIn, LogOut, Mail, MapPin, MapPinPlus, Map, Megaphone,
  MessageCircle, Mic, Microscope, Mountain, Move3d, Navigation, Newspaper, PartyPopper, Pencil,
  Phone, Plus, Printer, RefreshCw, Recycle, Repeat, Ruler, Save, School, Send, Settings2, Share2,
  Shield, ShieldAlert, ShieldCheck, ShieldPlus, ShoppingBag, Shirt, Signal, Siren, Sparkles,
  SprayCan, Star, Stethoscope, Store, Sun, Target, Tent, Trash2, TrendingUp, TriangleAlert,
  Trophy, Truck, Undo2, Upload, User, UserPlus, UserRoundCheck, Users, Volume2, Waves, Wifi,
  Wind, X, Zap,
} from "lucide-react";

/**
 * Material Symbol name → lucide component.
 *
 * Chosen for meaning rather than for looking like the old glyph. Where the two
 * disagreed, the clearer clinical read won: `ecg_heart` is the module's own
 * mark and becomes HeartPulse; `flood` and `water_damage` become Waves and
 * Droplets rather than a generic warning triangle, because the hazard type is
 * the information.
 */
export const ICONS = {
  // ── Navigation and chrome ──────────────────────────────────────────────────
  home: House,
  map: Map,
  menu_book: BookOpen,
  person: User,
  arrow_back: ArrowLeft,
  arrow_forward: ArrowRight,
  chevron_right: ChevronRight,
  close: X,
  cancel: CircleX,
  add: Plus,
  check: Check,
  task_alt: CheckCheck,
  check_circle: CircleCheck,
  refresh: RefreshCw,
  restart_alt: Repeat,
  undo: Undo2,
  send: Send,
  link: Link2,
  content_copy: Copy,
  print: Printer,
  download: Download,
  publish: Upload,
  folder_zip: Files,
  library_add: CirclePlus,
  tune: Settings2,
  layers: Layers,
  style: LayoutGrid,
  touch_app: Move3d,
  blur_on: Grid2x2,
  image: Image,
  article: Newspaper,
  description: FileText,
  event: CalendarDays,
  schedule: Clock,
  pending: Clock,
  hourglass_top: LoaderCircle,

  // ── Environmental health: the module's own vocabulary ──────────────────────
  ecg_heart: HeartPulse,
  health_and_safety: ShieldPlus,
  local_hospital: Stethoscope,
  science: FlaskConical,
  dataset: Database,
  quiz: ClipboardList,
  assignment: ClipboardList,
  assignment_turned_in: ClipboardCheck,
  monitoring: ChartLine,
  insights: ChartColumn,
  leaderboard: ChartPie,
  trending_up: TrendingUp,
  policy: Shield,
  gavel: Gavel,
  shield: Shield,
  shield_person: ShieldCheck,
  verified_user: BadgeCheck,
  admin_panel_settings: ShieldAlert,
  engineering: Hammer,
  report: FileText,
  add_alert: BellRing,

  // ── Hazard types (schema.js) ───────────────────────────────────────────────
  water: Droplet,
  water_drop: Droplet,
  water_damage: Droplets,
  opacity: Droplets,
  flood: Waves,
  waves: Waves,
  storm: Wind,
  landslide: Mountain,
  beach_access: Sun,
  set_meal: Fish,
  cruelty_free: Fish,
  scuba_diving: Fish,
  whatshot: Flame,
  local_fire_department: Flame,
  delete: Trash2,
  delete_sweep: Trash2,
  delete_forever: Trash2,
  recycling: Recycle,
  cleaning_services: Brush,
  sanitizer: SprayCan,
  eco: Leaf,
  public: Globe,
  travel_explore: Compass,

  // ── Risk and status ────────────────────────────────────────────────────────
  warning: TriangleAlert,
  error: CircleAlert,
  emergency: Siren,
  info: Info,
  flag: Flag,
  bolt: Zap,

  // ── Location ───────────────────────────────────────────────────────────────
  location_on: MapPin,
  add_location_alt: MapPinPlus,
  my_location: Crosshair,
  navigation: Navigation,

  // ── People ─────────────────────────────────────────────────────────────────
  group: Users,
  groups: Users,
  wc: Users,
  male: User,
  female: User,
  diversity_3: Users,
  person_add: UserPlus,
  how_to_reg: UserRoundCheck,
  volunteer_activism: HandHeart,
  record_voice_over: Mic,
  waving_hand: HandHeart,

  // ── Comms ──────────────────────────────────────────────────────────────────
  campaign: Megaphone,
  notifications: Bell,
  notifications_active: BellRing,
  notifications_none: BellOff,
  chat: MessageCircle,
  forum: MessageCircle,
  radio: Volume2,
  language: Languages,
  translate: Languages,
  mail: Mail,

  // ── Auth and account ───────────────────────────────────────────────────────
  login: LogIn,
  logout: LogOut,
  lock: LockKeyhole,
  lock_reset: LockKeyhole,
  visibility: Eye,
  visibility_off: EyeOff,
  badge: IdCard,
  photo_camera: Camera,
  add_a_photo: Camera,

  // ── Gamified layer (legacy waste app) ──────────────────────────────────────
  stars: Star,
  military_tech: Award,
  emoji_events: Trophy,
  workspace_premium: Award,
  card_giftcard: Gift,
  storefront: Store,
  celebration: PartyPopper,
  diamond: Gem,
  school: School,
  backpack: Backpack,
  checkroom: Shirt,
  glove: HandHeart,
  church: Building2,
  phone_android: Phone,
  signal_cellular_alt: Signal,
  wifi: Wifi,
  cloud_off: CloudOff,
  cloud_done: Cloud,
  cloud_upload: Upload,
  sync_problem: CircleAlert,
  save: Save,
  edit_note: Pencil,
  auto_awesome: Sparkles,
  priority_high: TriangleAlert,
  remove: Ruler,
  help: CircleHelp,
  ecg: Activity,
  target: Target,
  inbox: Archive,
  filter: Filter,
  boxes: Boxes,
  truck: Truck,
  tent: Tent,
  share: Share2,
  graduation: GraduationCap,
  microscope: Microscope,
  list: ListChecks,
  file_down: FileDown,
  shopping: ShoppingBag,
  airplay: Airplay,
  alarm: AlarmClock,
  sun: Sun,
};

/** Fallback for any name the map does not cover. */
export const FALLBACK_ICON = CircleHelp;

export const iconFor = (name) => ICONS[name] || FALLBACK_ICON;
